import { defineStore } from 'pinia';
import GeoMoverDB from '@/db/geoMoverDB';
import { appDebugLog } from '@/utils/appDebugLog';
import { withRoutingProviderDefaults } from '@/services/route-correction/providerDefaults';

export const useRouteCorrectionStore = defineStore('routeCorrection', {
  state: () => ({
    providers: [],
    loading: false,
    loaded: false,
    activeProviderSlug: null,
    testState: {
      running: false,
      providerSlug: null,
      ok: null,
      message: ''
    }
  }),

  getters: {
    sortedProviders: (state) => {
      const providers = Array.isArray(state.providers) ? state.providers : [];
      return [...providers].sort((a, b) => {
        const pa = Number.isFinite(Number(a?.priority)) ? Number(a.priority) : 999;
        const pb = Number.isFinite(Number(b?.priority)) ? Number(b.priority) : 999;
        if (pa !== pb) return pa - pb;
        return String(a?.title || '').localeCompare(String(b?.title || ''));
      });
    },

    activeProvider: (state) => {
      const providers = Array.isArray(state.providers) ? state.providers : [];
      if (state.activeProviderSlug) {
        const selected = providers.find((item) => item.slug === state.activeProviderSlug);
        if (selected) return selected;
      }
      return providers.find((item) => item.active) || providers[0] || null;
    }
  },

  actions: {
    async fetchProviders () {
      this.loading = true;
      try {
        const rows = await GeoMoverDB.findAll('routingProviders', 'priority', 'next');
        this.providers = Array.isArray(rows)
          ? rows.map((provider) => withRoutingProviderDefaults(provider))
          : [];
        this.loaded = true;

        const active = this.providers.find((item) => item.active);
        if (active) this.activeProviderSlug = active.slug;

        return this.providers;
      }
      finally {
        this.loading = false;
      }
    },

    setActiveProviderSlug (slug) {
      this.activeProviderSlug = slug;
    },

    async setProviderActive (id, active) {
      const nextActive = Boolean(active);
      const target = this.providers.find((provider) => provider.id === id);
      if (!target) return false;

      await GeoMoverDB.updateOne('routingProviders', { active: nextActive, updatedAt: Date.now() }, id);
      await this.fetchProviders();

      if (nextActive) this.activeProviderSlug = target.slug;
      else if (this.activeProviderSlug === target.slug) this.activeProviderSlug = null;
      return true;
    },

    async updateProviderConfig (id, partialConfig = {}) {
      const provider = this.providers.find((item) => item.id === id);
      if (!provider) return false;

      const nextConfig = {
        ...(provider.config || {}),
        ...partialConfig
      };

      await GeoMoverDB.updateOne('routingProviders', {
        config: nextConfig,
        updatedAt: Date.now()
      }, id);

      await this.fetchProviders();
      return true;
    },

    async updateProviderAuthMode (id, authMode) {
      return this.updateProviderConfig(id, { authMode });
    },

    async runConnectionTest (providerSlug, tester) {
      this.testState = {
        running: true,
        providerSlug,
        ok: null,
        message: 'Testing connection...'
      };

      try {
        const provider = this.providers.find((item) => item.slug === providerSlug);
        if (!provider) throw new Error(`Unknown provider: ${providerSlug}`);
        if (typeof tester !== 'function') throw new Error('No tester function provided');

        const result = await tester(provider);
        this.testState = {
          running: false,
          providerSlug,
          ok: Boolean(result?.ok),
          message: result?.message || (result?.ok ? 'Connection successful.' : 'Connection failed.')
        };

        return this.testState;
      }
      catch (error) {
        appDebugLog.warn('routeCorrectionStore', 'Connection test failed:', error?.message || error);
        this.testState = {
          running: false,
          providerSlug,
          ok: false,
          message: error?.message || 'Connection test failed.'
        };
        return this.testState;
      }
    }
  }
});
